import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CompanyDTO,
  Nullable,
  TeamInstructionConversationDTO,
  TeamInstructionDTO,
} from "@shaarp/common";
import { useEffect, useState } from "react";
import { RiExternalLinkLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { ToggleAdminMode } from "../admin/toggle-admin-mode";
import { ButtonLoading } from "../button-loading";
import CompanySelector from "../company/company-selector";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export function TeamSettingCompanyLinked({
  onCompanyChanged,
}: {
  onCompanyChanged?: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { isAdminMode } = useAdminMode();
  const { teamCompanySelected } = useTeams();
  const { t } = useTranslation();

  const [company, setCompany] = useState<Nullable<CompanyDTO>>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResetConfirm, setShowResetConfirm] = useState<boolean>(false);
  const [hasExistingInstructions, setHasExistingInstructions] =
    useState<boolean>(false);

  useEffect(() => {
    if (teamCompanySelected) {
      setCompany(teamCompanySelected);
    }
  }, [teamCompanySelected]);

  const hasCompanyChanged = company?.id !== teamCompanySelected?.id;

  const save = async (resetInstructions: boolean = false) => {
    try {
      setIsLoading(true);
      await api.post(`/my/team/company`, {
        companyId: company?.id || null,
        resetInstructions,
      });
      toast({
        variant: "default",
        title: t("team.components.companyLinked.toast.success.title"),
        description: t(
          "team.components.companyLinked.toast.success.description"
        ),
      });
      if (resetInstructions) {
        onCompanyChanged?.();
      }
    } catch (_) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!hasCompanyChanged) {
      await save();
      return;
    }

    try {
      const [conversationRes, instructionRes] = await Promise.all([
        api.get<Nullable<TeamInstructionConversationDTO>>(
          "/my/team/instruction/conversation"
        ),
        api.get<Nullable<TeamInstructionDTO>>("/my/team/instruction"),
      ]);

      const hasConversation = !!conversationRes.data;
      const hasInstructions = !!instructionRes.data?.content;
      setHasExistingInstructions(hasInstructions);

      if (hasConversation || hasInstructions) {
        setShowResetConfirm(true);
      } else {
        await save();
      }
    } catch (_) {
      await save();
    }
  };

  const handleConfirmSave = async () => {
    setShowResetConfirm(false);
    await save(hasExistingInstructions);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t("team.components.companyLinked.title")}</CardTitle>
          <CardDescription
            dangerouslySetInnerHTML={{
              __html: t("team.components.companyLinked.description"),
            }}
          />
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <CompanySelector
              company={company}
              onChange={setCompany}
              disabled={!isAdminMode}
            />
            {company && (
              <Tooltip>
                <TooltipTrigger>
                  <Button type="button" variant="outline" size="icon" asChild>
                    <Link to={`/company/${company?.id}`} target="_blank">
                      <RiExternalLinkLine />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("team.components.companyLinked.openInNewTab")}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </CardContent>
        <ToggleAdminMode>
          <CardFooter className="px-6 py-4 border-t">
            <ButtonLoading onClick={handleSave} isLoading={isLoading}>
              {t("team.components.companyLinked.saveButton")}
            </ButtonLoading>
          </CardFooter>
        </ToggleAdminMode>
      </Card>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                hasExistingInstructions
                  ? "team.components.companyLinked.resetInstructionsConfirm.title"
                  : "team.components.companyLinked.resetConversationConfirm.title"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                hasExistingInstructions
                  ? "team.components.companyLinked.resetInstructionsConfirm.description"
                  : "team.components.companyLinked.resetConversationConfirm.description"
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(
                "team.components.companyLinked.resetConversationConfirm.cancel"
              )}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>
              {t(
                "team.components.companyLinked.resetConversationConfirm.confirm"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
